/**
 * The recent conversion sends: when, who, what, where, and how it went.
 * A table from tablet width up, stacked cards on a phone.
 */
import { Link, useLocation } from "react-router-dom";
import { Send as SendIcon } from "lucide-react";
import { Empty, Pill, timeAgo, usd } from "@/features/command/ui";
import { outcomeLabel, plainDetail, platformInfo, type Send } from "./api";

const STATUS: Record<string, { label: string; color: string }> = {
  sent: { label: "Sent", color: "emerald" },
  failed: { label: "Failed", color: "rose" },
  skipped: { label: "Skipped", color: "zinc" },
  sending: { label: "Sending", color: "amber" },
};

export function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, color: "zinc" };
  return <Pill color={s.color}>{s.label}</Pill>;
}

function What({ s }: { s: Send }) {
  const value = Number(s.value_usd ?? 0);
  return (
    <span className="text-zinc-200">
      {outcomeLabel(s.event)}
      {value > 0 && <span className="ml-1 tabular-nums text-emerald-400">{usd(value, { cents: value % 1 !== 0 })}</span>}
    </span>
  );
}

function Who({ s, from }: { s: Send; from: string }) {
  const name = s.contact_name?.trim() || "Someone";
  if (!s.contact_id) return <span className="text-zinc-500">{name}</span>;
  return <Link to={`/contacts/${s.contact_id}`} state={{ from }} className="text-indigo-300 hover:underline">{name}</Link>;
}

function where(s: Send): string {
  const p = platformInfo(s.platform).label;
  return s.destination_name ? `${p} · ${s.destination_name}` : p;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function SendsLog({ sends }: { sends: Send[] }) {
  const { pathname, search } = useLocation();
  const from = `${pathname}${search}`;
  if (sends.length === 0) {
    return (
      <Empty icon={SendIcon} title="Nothing sent yet">
        Each signup, trial start and payment that goes to an ad platform shows up here, with whether it was accepted.
      </Empty>
    );
  }
  return (
    <>
      {/* Tablet and up: a table that scrolls inside its own box if it must. */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              <th className="pb-2 pr-3 font-semibold">When</th>
              <th className="pb-2 pr-3 font-semibold">Who</th>
              <th className="pb-2 pr-3 font-semibold">What</th>
              <th className="pb-2 pr-3 font-semibold">Where</th>
              <th className="pb-2 font-semibold">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/70">
            {sends.map((s) => (
              <tr key={String(s.id)} className="align-top">
                <td className="whitespace-nowrap py-2.5 pr-3 text-zinc-400" title={when(s.created_at)}>{timeAgo(s.created_at)}</td>
                <td className="py-2.5 pr-3"><Who s={s} from={from} /></td>
                <td className="whitespace-nowrap py-2.5 pr-3"><What s={s} /></td>
                <td className="py-2.5 pr-3 text-zinc-300">{where(s)}</td>
                <td className="py-2.5">
                  <div className="flex flex-col items-start gap-1">
                    <StatusPill status={s.status} />
                    {s.detail && <span className="max-w-[22rem] text-xs leading-snug text-zinc-500">{plainDetail(s.detail)}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone: one card per send. */}
      <div className="space-y-2 sm:hidden">
        {sends.map((s) => (
          <div key={String(s.id)} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5"><What s={s} /><span className="text-zinc-600">·</span><Who s={s} from={from} /></span>
              <StatusPill status={s.status} />
            </div>
            <div className="mt-1 text-xs text-zinc-500">{where(s)} · {timeAgo(s.created_at)}</div>
            {s.detail && <div className="mt-1 text-xs leading-snug text-zinc-500">{plainDetail(s.detail)}</div>}
          </div>
        ))}
      </div>
    </>
  );
}
